// v1 View Requisition actions reused by v2 (same endpoints/payloads as SingleRequisition/ViewReq).
import { Row } from "../types";
import { pick, rowText } from "../utils";
import { base64ToExcel } from "Pages/DynamicGrid/bulkExportActions";
import { BrotherPrint, DymoMultiPrint, ZebraMultiPrint, getPrinterContentData } from "./printing";
import RequisitionType from "Services/Requisition/RequisitionTypeService";

export interface PrinterInfo {
  label: string;
  value: number | string;
  isDefault?: boolean;
}

export interface RejectionReasonOption {
  label: string;
  value: number | string;
}

type Res = { data?: any; status?: number };

const ok = (res: Res) =>
  res?.status === 200 ||
  res?.data?.httpStatusCode === 200 ||
  res?.data?.statusCode === 200;

const ids = (rows: Row[]) =>
  rows.map((r) => Number(pick(r, "RequisitionOrderId", "__RowKey")));

export class ActionError extends Error {}

const fail = (res: Res, fallback: string): never => {
  throw new ActionError(res?.data?.message || res?.data?.Message || fallback);
};

const actionsApi = {
  // ------------------------------------------------------------ printers / labels
  async printers(): Promise<PrinterInfo[]> {
    const res: Res = await RequisitionType.GetPrintersInfo();
    return (res?.data?.data ?? []) as PrinterInfo[];
  },

  // GetPrinterContent → Zebra / Dymo / Brother by printer label (same as v1 printLabel).
  async printLabels(printer: PrinterInfo, rows: Row[]): Promise<boolean> {
    const content = await getPrinterContentData({
      printerId: printer.value,
      contentList: rows.map((r) => ({
        requisitionOrderId: rowText(r, "RequisitionOrderId", "__RowKey"),
        requisitionId: rowText(r, "RequisitionId"),
      })),
    });
    if (!content) return false; // getPrinterContentData already showed the error
    const label = printer.label.toLowerCase();
    if (label.includes("zebra")) await ZebraMultiPrint(content);
    else if (label.includes("dymo")) await DymoMultiPrint(content);
    else if (label.includes("brother")) await BrotherPrint(content);
    else throw new ActionError("Configuration not available");
    return true;
  },

  // ------------------------------------------------------------ records / reports (open in /docs-viewer)
  async printRecords(rows: Row[]) {
    const res: Res = await RequisitionType.PrintRecordsV2(ids(rows));
    if (!ok(res)) fail(res, "Could not print the selected records.");
    return res.data as { message?: string; data: string | string[] };
  },
  async printReports(rows: Row[]) {
    const res: Res = await RequisitionType.PrintReportsV2(ids(rows));
    if (!ok(res)) fail(res, "Could not print the selected reports.");
    return res.data as { message?: string; data: string | string[] };
  },
  async printRecordsAndReports(rows: Row[]) {
    const res: Res = await RequisitionType.PrintRecordsAndReports(ids(rows));
    if (!ok(res)) fail(res, "Could not print the selected records and reports.");
    return res.data as { message?: string; data: string | string[] };
  },

  // Row "Print": PrintRecords → ShowBlob → opens the file in a new tab.
  async printSingleRecord(row: Row) {
    const res: Res = await RequisitionType.PrintSelectedRecords(ids([row]));
    if (!ok(res)) fail(res, "Could not print this record.");
    const blob: Res = await RequisitionType.ShowBlob(res.data.data);
    const url = String(blob?.data?.Data ?? "").replace("}", "");
    if (url) window.open(url, "_blank");
    return res.data?.message as string | undefined;
  },

  // ------------------------------------------------------------ excel export (Export_To_ExcelV2)
  async exportExcel(payload: {
    tabId: number;
    sortColumn?: string | null;
    sortDirection?: string | null;
    filters: unknown[];
    selectedRow?: number[];
  }) {
    const res: Res = await RequisitionType.viewRequisitionExportToExcelV2(payload);
    if (!ok(res)) fail(res, "Export failed.");
    base64ToExcel(res.data.data.fileContents, "View Requisition");
    return res.data?.message as string | undefined;
  },

  // ------------------------------------------------------------ bulk actions
  async transferToZoho(rows: Row[]) {
    const res: Res = await RequisitionType.transferRecords(
      ids(rows),
      "TransferOrderToZoho"
    );
    if (!ok(res)) fail(res, "Transfer failed.");
    return res.data?.message as string | undefined;
  },

  async updateDateOfCollection(rows: Row[], dateOfCollection: string) {
    const res: Res = await RequisitionType.UpdateDateofCollection({
      requisitionOrderIds: ids(rows),
      dateOfCollection,
    });
    if (!ok(res)) fail(res, "Could not update the date of collection.");
    return res.data?.message as string | undefined;
  },

  // Next step prerequisites (v1 DataUpdateModal): date/time of collection + accession #.
  async updateCollectionInfo(
    row: Row,
    values: { dateOfCollection: string; timeOfCollection: string; accessionNo?: string }
  ) {
    const formData = new FormData();
    formData.append("DateOfCollection", values.dateOfCollection);
    formData.append("TimeOfCollection", values.timeOfCollection);
    formData.append("NextStep", rowText(row, "NextStep"));
    formData.append("RequisitionId", rowText(row, "RequisitionId"));
    formData.append("RequisitionOrderId", rowText(row, "RequisitionOrderId", "__RowKey"));
    formData.append("RequisitionType", rowText(row, "RequisitionType"));
    if (values.accessionNo) formData.append("AccessionNo", values.accessionNo);
    await RequisitionType.updateRequisitionCollectionDateAndTime(formData);
  },

  // ------------------------------------------------------------ row actions
  async duplicate(row: Row) {
    const res: Res = await RequisitionType.DuplicateRecord({
      requisitionId: pick(row, "RequisitionId"),
      requisitionorderid: pick(row, "RequisitionOrderId", "__RowKey"),
    });
    if (!ok(res)) fail(res, "Could not duplicate this requisition.");
    return res.data as {
      message?: string;
      requisitionId: number;
      requisitionOrderId: number;
    };
  },

  async restore(row: Row) {
    const res: Res = await RequisitionType.RestoreRequisition(
      pick(row, "RequisitionOrderId", "__RowKey")
    );
    if (!ok(res)) fail(res, "Could not restore this requisition.");
    return res.data?.message as string | undefined;
  },

  async flagDetails(row: Row, flagId: number) {
    const res: Res = await RequisitionType.GetRequisitionFlagDetails({
      flagId,
      requisitionOrderId: Number(pick(row, "RequisitionOrderId", "__RowKey")),
      requisitionId: Number(pick(row, "RequisitionId")),
    });
    return res?.data?.data || res?.data;
  },

  // ------------------------------------------------------------ rejection reasons (v1 Reject modal)
  async rejectionReasons(): Promise<RejectionReasonOption[]> {
    const res: Res = await RequisitionType.GetRejectionReasonLookupV2();
    return (res?.data?.data ?? res?.data ?? []) as RejectionReasonOption[];
  },
  async rejectionReasonSubject(label: string): Promise<string> {
    const res: Res = await RequisitionType.ShowRejectionReason({ item1: label });
    return res?.data?.subject ?? label;
  },
  async addRejectionReason(subject: string, description: string) {
    const res: Res = await RequisitionType.AddRejectionReason({
      text: subject,
      subject,
      description,
      requisitionAssignment: [],
    });
    if (!ok(res)) fail(res, "Could not add the reason.");
  },
  async deleteRejectionReason(id: number | string) {
    const res: Res = await RequisitionType.RequisitionRejectionDelete(id);
    if (!ok(res)) fail(res, "Could not delete the reason.");
  },
  // After a Reject status change: assign the reason to each requisition (v1 handleSaveAgainstReason).
  async saveRejectionAssignment(rows: Row[], subject: string) {
    await RequisitionType.SaveRequisitionAgainstReason({
      assignments: rows.map((r) => ({
        requisitionId: pick(r, "RequisitionId"),
        requisitionOrderId: pick(r, "RequisitionOrderId", "__RowKey"),
        requisitionType: pick(r, "RequisitionTypeId"),
      })),
      rejection: subject,
    });
  },
};

export default actionsApi;
